/**
 * Branded skeleton primitives — drop-in replacements for the bare
 * `LinearProgress` bar most pages show while their first paint
 * arrives. Three flavors that cover ~95% of loading states:
 *
 *     <RowSkeleton />              one list / table row
 *     <CardSkeleton />             one card tile
 *     <TableSkeleton rows={6} />   N rows stacked
 *     <PageSkeleton />             full-page placeholder
 *
 * All animate via MUI's wave variant so the page never looks dead.
 * Heights match the final content so when data arrives there's no
 * layout shift.
 */
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';


export function RowSkeleton() {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.5,
      p: 1, borderRadius: 1.5,
    }}>
      <Skeleton variant="circular" width={28} height={28} animation="wave" />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" sx={{ fontSize: 14, width: '40%' }} animation="wave" />
        <Skeleton variant="text" sx={{ fontSize: 12, width: '25%' }} animation="wave" />
      </Box>
      <Skeleton variant="rounded" width={48} height={20} animation="wave" />
    </Box>
  );
}


export function CardSkeleton({ height = 160 }: { height?: number }) {
  return (
    <Box sx={{
      p: 2, borderRadius: 3,
      bgcolor: 'background.paper',
      border: '1px solid', borderColor: 'divider',
      display: 'flex', flexDirection: 'column', gap: 1,
      height,
    }}>
      <Skeleton variant="text" sx={{ width: '50%', fontSize: 14 }} animation="wave" />
      <Skeleton variant="rounded" sx={{ flex: 1 }} animation="wave" />
    </Box>
  );
}


export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <RowSkeleton key={i} />
      ))}
    </Box>
  );
}


/** Full-page placeholder used by route-level Suspense / loading
 * fallbacks. Same column layout most dashboard pages use. */
export function PageSkeleton() {
  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Skeleton variant="rounded" height={120} animation="wave" />
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 2,
      }}>
        {[1, 2, 3, 4].map((i) =>
          <Skeleton key={i} variant="rounded" height={80} animation="wave" />
        )}
      </Box>
      <TableSkeleton rows={6} />
    </Box>
  );
}
